import { StorageAdapter } from '../../storage/StorageAdapter'

/**
 * upsert.js — el upsert TRANSACCIONAL por lotes del pull Tier B (PULL-05).
 *
 * Dos helpers, SQL SOLO via el `db` inyectado (default = StorageAdapter real, STOR-05):
 *   - extractPage(body): normaliza la paginacion Laravel a { rows, hasMore }.
 *   - upsertCatalog({...}): escribe N filas en chunks multi-row INSERT ... ON CONFLICT
 *     dentro de UNA sola transaccion por catalogo (sin N+1), con full-replace diff-delete
 *     atomico en la MISMA tx.
 *
 * MECANISMO DE DIFF-DELETE (LOCKED — full-replace dentro de una sola tx):
 *   En full-refresh borramos TODA la tabla (o el subconjunto por scope si el descriptor
 *   trae whereSql) y re-insertamos el set fresco, todo en la MISMA transaccion. Un crash
 *   a mitad del pull hace ROLLBACK al cache bueno previo (atomico; nunca una tabla a medio
 *   borrar). Se eligio frente a:
 *     - columna-epoch (`DELETE WHERE pulled_epoch != N`): exigiria una migracion toVersion:2.
 *     - `DELETE WHERE id NOT IN (...)`: explota el limite de bind params con miles de ids.
 *   full-replace-en-una-tx es la opcion portable-correcta mas simple.
 *
 * SEGURIDAD DE INPUT VACIO (apoya el empty-response guard B1 que vive en pull-engine):
 *   rows.length === 0 + fullRefresh => NO emitimos el DELETE (un full refresh de 0 filas
 *   debe ser un NO-OP a este nivel; nunca `DELETE FROM table` sin nada que re-insertar).
 *   El guard del pull-engine es la defensa B1 primaria; esto es cinturon-y-tirantes para que
 *   ni una llamada directa `upsertCatalog([])` pueda vaciar una tabla.
 *
 * LIMITE DE PARAMS: <= 900 bind params por chunk (piso portable vs el 999 de Android viejo,
 * ver 05-RESEARCH.md "Batched Upserts"). sql.js/iOS soportan 32766 pero 900 es correcto en
 * todos lados.
 *
 * ON CONFLICT DO UPDATE esta VERIFICADO funcionando en sql.js 1.11.0 (STATE.md [03-01],
 * upsert-capability.spec.js) — no se necesita fallback SELECT-then-write.
 */

const PARAM_FLOOR = 900

/**
 * Normalizador de paginacion Laravel (re-impl limpia de v1 _catalog.js:21, NO portado).
 * @param {*} body cuerpo de respuesta crudo
 * @returns {{ rows: Array, hasMore: boolean }}
 */
export function extractPage (body) {
  if (!body) return { rows: [], hasMore: false }

  // Array plano => una sola pagina.
  if (Array.isArray(body)) return { rows: body, hasMore: false }

  const data = body.data

  // Doble-wrap que usan algunos endpoints gql: body.data.data es el array real
  // (inventario 05-RESEARCH.md: forms groups devuelve data.data.data).
  if (data && !Array.isArray(data) && Array.isArray(data.data)) {
    const outer = body
    const cp = Number(outer.current_page)
    const lp = Number(outer.last_page)
    const hasMore = Number.isFinite(cp) && Number.isFinite(lp) ? cp < lp : false
    return { rows: data.data, hasMore }
  }

  // Single-wrap Laravel paginate: { data: [...], current_page, last_page }.
  if (Array.isArray(data)) {
    const cp = Number(body.current_page)
    const lp = Number(body.last_page)
    const hasMore = Number.isFinite(cp) && Number.isFinite(lp) ? cp < lp : false
    return { rows: data, hasMore }
  }

  return { rows: [], hasMore: false }
}

/**
 * Upsert batched + full-replace diff-delete, UNA transaccion por llamada.
 *
 * @param {object} opts
 * @param {string}   opts.table        tabla c_* destino.
 * @param {string[]} opts.keyColumns   columnas del conflict-target (default ['id']).
 * @param {string[]} opts.columns      todas las columnas a escribir.
 * @param {object[]} opts.rows         filas mapeadas (keyed por columns).
 * @param {boolean}  opts.fullRefresh  true (default) = borra el set previo en la misma tx.
 * @param {string}   [opts.whereSql]   predicado opcional para acotar el DELETE de full-refresh (scope).
 * @param {Array}    [opts.whereParams] params del whereSql.
 * @param {number[]} [opts.deletedIds] (DISENADO-IN, OFF en F4) ids a borrar cuando fullRefresh=false.
 * @param {object}   [executor]
 * @param {object}   [executor.db]     ejecutor inyectable (default StorageAdapter).
 * @returns {Promise<{upserted:number, chunks:number, noop?:boolean}>}
 */
export async function upsertCatalog (
  { table, keyColumns = ['id'], columns, rows, fullRefresh = true, whereSql, whereParams = [], deletedIds = [] },
  { db = StorageAdapter } = {}
) {
  // NO-OP de full-refresh vacio: nunca borrar sin nada que re-insertar (apoya B1).
  if (rows.length === 0 && fullRefresh) {
    return { upserted: 0, chunks: 0, noop: true }
  }

  const perChunk = Math.max(1, Math.floor(PARAM_FLOOR / columns.length))
  // NO-CONFLICT (aditivo 05-02): keyColumns vacio => INSERT plano sin clausula ON CONFLICT. Lo usa
  // c_areas (name-keyed sin UNIQUE/PK sobre name → ON CONFLICT(name) seria invalido en SQLite). Es
  // seguro porque full-refresh borra la tabla ANTES del INSERT (no hay filas previas con que chocar);
  // los names del set son unicos. Para el resto, ON CONFLICT(<keys>) sigue igual que en 05-01.
  const hasConflictTarget = keyColumns && keyColumns.length > 0
  const nonKey = hasConflictTarget ? columns.filter((c) => !keyColumns.includes(c)) : columns
  const onConflict = hasConflictTarget
    ? ' ON CONFLICT(' + keyColumns.join(',') + ') ' + (nonKey.length
      ? 'DO UPDATE SET ' + nonKey.map((c) => `${c}=excluded.${c}`).join(', ')
      : 'DO NOTHING') // todas las columnas son conflict-target (defensivo, espeja [03-01] _buildUpsert)
    : '' // sin conflict target => INSERT plano (full-refresh ya limpio la tabla)

  let chunks = 0

  await db.transaction(async (handle) => {
    if (fullRefresh) {
      // Full-replace: borra el set previo (todo, o el scope via whereSql) en la MISMA tx.
      const deleteSql = whereSql ? `DELETE FROM ${table} WHERE ${whereSql}` : `DELETE FROM ${table}`
      await handle.run(deleteSql, whereSql ? whereParams : [], false)
    } else if (deletedIds.length) {
      // Path delta (DISENADO-IN, no ejercitado en F4): borra exactamente los ids tombstone en chunks.
      const idPerChunk = PARAM_FLOOR
      for (let i = 0; i < deletedIds.length; i += idPerChunk) {
        const slice = deletedIds.slice(i, i + idPerChunk)
        const ph = slice.map(() => '?').join(',')
        await handle.run(`DELETE FROM ${table} WHERE id IN (${ph})`, slice, false)
      }
    }

    // INSERT multi-row chunked (sin N+1).
    for (let i = 0; i < rows.length; i += perChunk) {
      const chunk = rows.slice(i, i + perChunk)
      const groups = chunk.map(() => `(${columns.map(() => '?').join(',')})`).join(',')
      const params = chunk.flatMap((row) => columns.map((c) => row[c]))
      const sql =
        `INSERT INTO ${table} (${columns.join(',')}) VALUES ${groups}` + onConflict
      await handle.run(sql, params, false)
      chunks += 1
    }
  })

  return { upserted: rows.length, chunks }
}
